import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  AdminData, 
  GetAdminsResponse, 
  AdminFilters, 
  AdminForm, 
  AdminUpdateForm,
  RequestOtpRequest,
  VerifyOtpRequest,
  VerifyOtpResponse
} from '../../types';
import apiService from '../../services/api';

interface AdminsState {
  admins: AdminData[];
  selectedAdmin: AdminData | null;
  currentAdmin: AdminData | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: AdminFilters;
  loading: boolean;
  error: string | null;
}

const initialState: AdminsState = {
  admins: [],
  selectedAdmin: null,
  currentAdmin: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  filters: {},
  loading: false,
  error: null,
};

export const fetchAdmins = createAsyncThunk(
  'admins/fetchAdmins',
  async ({ page, limit, filters }: { page: number; limit: number; filters?: AdminFilters }) => {
    const response = await apiService.getAdmins(page, limit, filters);
    return response;
  }
);

export const fetchAdminDetails = createAsyncThunk(
  'admins/fetchAdminDetails',
  async (adminId: string) => {
    const response = await apiService.getAdminProfile();
    return response;
  }
);

export const getCurrentAdmin = createAsyncThunk(
  'admins/getCurrentAdmin',
  async () => {
    const response = await apiService.getAdminProfile();
    return response;
  }
);

export const createAdmin = createAsyncThunk(
  'admins/createAdmin',
  async (adminData: AdminForm, { rejectWithValue }) => {
    try {
      const response = await apiService.createAdmin(adminData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.error ||
          error?.response?.data?.details ||
          error?.message ||
          'Failed to create admin'
      );
    }
  }
);

export const updateAdmin = createAsyncThunk(
  'admins/updateAdmin',
  async ({ adminId, adminData }: { adminId: string; adminData: AdminUpdateForm }) => {
    const response = await apiService.updateAdmin(adminId, adminData);
    return response;
  }
);

export const toggleAdminStatus = createAsyncThunk(
  'admins/toggleAdminStatus',
  async (adminId: string) => {
    const response = await apiService.toggleAdminStatus(adminId);
    return { adminId, response };
  }
);

export const deleteAdmin = createAsyncThunk(
  'admins/deleteAdmin',
  async (adminId: string) => {
    const response = await apiService.deleteAdmin(adminId);
    return { adminId, response };
  }
);

export const requestOtp = createAsyncThunk(
  'admins/requestOtp',
  async (requestData: RequestOtpRequest) => {
    const response = await apiService.requestOtp(requestData);
    return response;
  }
);

export const verifyOtp = createAsyncThunk(
  'admins/verifyOtp',
  async (verifyData: VerifyOtpRequest) => {
    const response = await apiService.verifyOtp(verifyData);
    return response;
  }
);

const adminsSlice = createSlice({
  name: 'admins',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<AdminFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setSelectedAdmin: (state, action: PayloadAction<AdminData | null>) => {
      state.selectedAdmin = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch admins
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as GetAdminsResponse;
        if (response.success && response.data) {
          state.admins = response.data;
          if (response.pagination) {
            state.pagination = {
              page: response.pagination.page,
              limit: response.pagination.limit,
              total: response.pagination.total,
              totalPages: response.pagination.pages,
            };
          }
        }
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch admins';
      })
      // Fetch admin details
      .addCase(fetchAdminDetails.fulfilled, (state, action) => {
        state.selectedAdmin = action.payload;
      })
      // Get current admin
      .addCase(getCurrentAdmin.fulfilled, (state, action) => {
        state.currentAdmin = action.payload;
      })
      // Create admin
      .addCase(createAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.admins.unshift(action.payload);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to create admin';
      })
      // Update admin
      .addCase(updateAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdmin.fulfilled, (state, action) => {
        state.loading = false;
        const updatedAdmin = action.payload;
        const index = state.admins.findIndex(admin => admin.id === updatedAdmin.id);
        if (index !== -1) {
          state.admins[index] = updatedAdmin;
        }
        if (state.selectedAdmin?.id === updatedAdmin.id) {
          state.selectedAdmin = updatedAdmin;
        }
        if (state.currentAdmin?.id === updatedAdmin.id) {
          state.currentAdmin = updatedAdmin;
        }
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update admin';
      })
      // Toggle admin status
      .addCase(toggleAdminStatus.fulfilled, (state, action) => {
        const { adminId } = action.payload;
        const admin = state.admins.find(a => a.id === adminId);
        if (admin) {
          admin.isActive = !admin.isActive;
        }
        if (state.selectedAdmin?.id === adminId) {
          state.selectedAdmin.isActive = !state.selectedAdmin.isActive;
        }
        if (state.currentAdmin?.id === adminId) {
          state.currentAdmin.isActive = !state.currentAdmin.isActive;
        }
      })
      // Delete admin
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        const { adminId } = action.payload;
        state.admins = state.admins.filter(admin => admin.id !== adminId);
        if (state.selectedAdmin?.id === adminId) {
          state.selectedAdmin = null;
        }
      })
      // Request OTP
      .addCase(requestOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to request OTP';
      })
      // Verify OTP
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as VerifyOtpResponse;
        if (response.success && response.admin) {
          state.currentAdmin = response.admin;
        }
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to verify OTP';
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setPage,
  setSelectedAdmin,
  clearError,
} = adminsSlice.actions;

export default adminsSlice.reducer; 